import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostListener,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from "rxjs/operators";

import { AutocompleteOption } from "../..";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => AutoCompleteComponent),
	multi: true,
};

@Component({
	selector: "ic-autocomplete",
	templateUrl: "./autocomplete.component.html",
	providers: [INPUT_CONTROL_ACCESSOR],
	styleUrls: ["./autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AutoCompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
	private cdRef = inject(ChangeDetectorRef);
	private eRef = inject(ElementRef);
	private fb = inject(UntypedFormBuilder);

	showList = false;
	disabled = false;
	innerForm: UntypedFormGroup = this.fb.group({
		value: [""],
		input: [""],
	});

	input$ = new Subject<string>();
	private destroy$ = new Subject<void>();

	@Input() icon: string;
	@Input() iconRight = false;
	@Input() loading = false;
	@Input() placeholder: string;
	@Input() label: string;
	@Input() required = true;
	@Input() help: string;
	@Input() missing = false;
	@Input() hideClear = false;
	@Input() missingText: string;
	@Input() maxlength: string;
	@Input() threshold = 3;
	@Input() items: AutocompleteOption[] = [];
	@Input() key: string;

	@Output() optionSelected = new EventEmitter<any>();
	@Output() valueChange = new EventEmitter<string>();

	@ContentChild("itemTmpl", { static: false }) itemTemplate: TemplateRef<any>;
	@ContentChild("loadingTmpl", { static: false }) loadingTemplate: TemplateRef<any>;
	@ContentChild("emptyTmpl", { static: false }) emptyTemplate: TemplateRef<any>;
	@ContentChild("valueTmpl", { static: false }) valueTemplate: TemplateRef<any>;

	@ViewChild("input", { static: true }) input: ElementRef;

	@Input() disableClickHandler: (item) => boolean = () => false;

	get value() {
		return this.innerForm.getRawValue().value;
	}

	@HostListener("document:click", ["$event"])
	clickout(event) {
		if (!this.eRef.nativeElement.contains(event.target)) {
			this.showList = false;
		}
	}

	selectOption(option: any) {
		if (this.disableClickHandler(option) || option.selectIsDisabled) {
			return;
		}

		const value = this.key ? option[this.key] : option;
		this.innerForm.patchValue({ value });
		this.showList = false;
		this.onTouch();
		this.onChange(value);
		this.optionSelected.emit(value);
		this.cdRef.markForCheck();
	}

	onFocus() {
		const { input } = this.innerForm.value;
		this.showList = this.threshold === 0 || (input && input.length > this.threshold);
	}

	focus() {
		this.input.nativeElement.focus();
	}

	writeValue(selected: string): void {
		if (!selected) {
			this.innerForm.reset();

			return;
		}

		this.innerForm.patchValue({
			value: selected,
			input: "",
		});
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;

		if (this.disabled) {
			this.innerForm.disable({ emitEvent: false });
		} else {
			this.innerForm.enable({ emitEvent: false });
		}

		this.cdRef.detectChanges();
	}

	clearForm() {
		this.onTouch();
		this.onChange(null);
		this.optionSelected.emit(null);

		this.innerForm.reset();
	}

	private onChange = (_: unknown) => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function

	ngOnInit() {
		this.innerForm
			.get("input")
			.valueChanges.pipe(
				takeUntil(this.destroy$),
				tap((value) => {
					if (value && value.length < this.threshold && this.showList) {
						this.showList = false;
					}
				}),
				filter((value) => (value || value === "") && value.length >= this.threshold),
				debounceTime(300),
				tap(() => {
					// Show the list before `distinctUntilChanged` so it shows the previous value if values are the same
					this.showList = true;
					this.cdRef.markForCheck();
				}),
				distinctUntilChanged(),
				tap((value) => {
					this.valueChange.emit(value);
				}),
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
